// ─────────────────────────────────────────────────────────────────────────────
// GATE identity — pure role model that decides which GATE options a person sees.
//
// CLIENT GATING IS UX-ONLY. Every destination the GATE routes into still
// enforces its own authz server-side; this module only decides what to *show*
// so a person isn't offered a door they can't open. No secrets gate on this.
//
// Tapestry: ~/.claude/tapestries/ggm-backend/12-gganvil-gate-onboarding
// ─────────────────────────────────────────────────────────────────────────────
import type { Me, OrganizationRole, Role } from '../../lib/api';

/** DEV-only preview personas (?as=<persona>) — lets the screen be eyeballed
 *  across roles without a live session. NOT used in production. */
export type GatePersona = 'public' | 'member' | 'leader' | 'orgadmin' | 'founder' | 'captain';

/** TIME axis — an OPEN submission window for this person, as resolved by
 *  GET /me/live-match (2026-07-26). No longer "reserved": this is now wired,
 *  and a non-null value puts the GATE into match-night mode — see gateDoors.ts.
 *
 *  compId is REQUIRED: the match-card page renders "Missing match or
 *  competition id." without it (Lisa, 2026-07-13).
 *
 *  Structurally identical to lib/api.ts's LiveMatchWindow, deliberately
 *  restated here rather than imported: this module is the pure role model and
 *  its types describe what the GATE needs, not what the wire happens to send.
 *  The preview personas below construct these without any API in play. */
export type LiveMatchRef = {
  matchId: string;
  compId: string;
  label: string;
  opponentClanName: string;
  /** How many screenshots this person is still on the hook for. */
  pendingSlotCount: number;
  /** 'own' their own slot · 'proxy' ghost seats they lead · 'both'. */
  reason: 'own' | 'proxy' | 'both';
};

export type GateIdentity = {
  displayName: string;
  role: Role;
  isFounder: boolean;
  /** member+ : has a place in the dashboard (a membership, a clan, or a non-public role). */
  isMember: boolean;
  /** may see BUILDING → competition (org owner/admin, platform admin, or founder). */
  canBuildComp: boolean;
  /** T3 (server-is-the-org, 2026-07-19): actually leads a clan — CLAN_OWNER
   *  ("Leader") or CLAN_LEADER ("Co-Admin"), the captains. Reuses the SAME
   *  coarse signal MyClanIntro/LeaderPanel key off (`role === 'leader'`) —
   *  the backend's getRole() stamps platform role 'leader' whenever the
   *  caller's clan-membership row carries legacy `role: 'LEADER'`, and
   *  promoting someone to CLAN_LEADER (co-admin) sets that SAME legacy field
   *  (portalApi/index.ts's updateClanMemberRole: `newLegacyRole = scopedRole
   *  === CLAN_MEMBER ? 'MEMBER' : 'LEADER'`) — so this one boolean already
   *  covers both captain tiers without a separate clan-detail fetch. */
  isClanLeader: boolean;
  /** Holds at least one ACTIVE team captaincy (F1, 2026-07-22). Deliberately
   *  SEPARATE from isClanLeader: a captain grant is an overlay that never
   *  promotes the base membership role, so a captain is `role: 'member'` and
   *  isClanLeader stays false for them. Conflating the two would silently hand
   *  captains every leader-gated surface; they get exactly one — see
   *  canEnterComp. */
  isTeamCaptain: boolean;
  /** May enter their clan into a competition — the GATE's "Competition as a
   *  clan" door. Mirrors the backend's register-my-clan gate
   *  (portalApi/index.ts): the clan LEADER, a FOUNDER, or the CAPTAIN of a
   *  named team (that last one entering their own team only, enforced
   *  server-side). Needs a clan in every case — with none, JoinCompScreen's
   *  "build a clan first" branch owns the person instead.
   *
   *  This replaced a bare isClanLeader check on the tile, which hid a shipped
   *  captain feature (JoinCompScreen's team-pick step) behind a door no captain
   *  could see, and hid the flow from founders the backend explicitly allows. */
  canEnterComp: boolean;
  /** Is a member of at least one org (any org role). Drives the JOINING
   *  screen's apply-flow tile for T3 — org members without a clan (or who
   *  aren't a leader) apply instead of forging/joining directly. */
  isOrgMember: boolean;
  /** TIME axis. null = nothing live right now (T-playnow not built yet). */
  liveMatch: LiveMatchRef | null;
};

const ORG_BUILDER_ROLES: OrganizationRole[] = ['ORG_OWNER', 'ORG_ADMIN'];

/** Derive the GATE identity from the authenticated `me` payload. */
export function resolveGateIdentity(
  me: Me | null | undefined,
  liveMatch: LiveMatchRef | null = null,
): GateIdentity {
  const role: Role = me?.role ?? 'public';
  const orgRoles = (me?.orgMemberships ?? []).map(m => m.role);
  const compRoles = (me?.competitionMemberships ?? []).map(m => m.role);
  const platformPriv = role === 'founder' || role === 'admin';
  const isClanLeader = role === 'leader';
  // Optional + additive: absent on any /me response cached from before the
  // field shipped, which reads as "no captaincies" — the same fail-closed
  // answer every gate assumed before it existed.
  const isTeamCaptain = (me?.captaincies ?? []).length > 0;
  const hasClan = Boolean(me?.clanName);
  return {
    displayName: me?.displayName ?? 'Guest',
    role,
    isFounder: role === 'founder',
    isMember: role !== 'public' || Boolean(me?.clanName) || orgRoles.length > 0 || compRoles.length > 0,
    canBuildComp: platformPriv || orgRoles.some(r => ORG_BUILDER_ROLES.includes(r)),
    isClanLeader,
    isTeamCaptain,
    // Mirrors register-my-clan: leader OR founder OR team captain, and a clan
    // in every case. Note 'admin' is deliberately absent — the backend's
    // bypass reads `role !== 'founder'` only, so a platform admin who merely
    // belongs to a clan is refused there and must be refused here too.
    canEnterComp: hasClan && (isClanLeader || role === 'founder' || isTeamCaptain),
    isOrgMember: orgRoles.length > 0,
    liveMatch,
  };
}

/** Synthetic identity for the DEV ?as=<persona> preview. */
export function previewIdentity(persona: GatePersona): GateIdentity {
  const map: Record<GatePersona, Omit<GateIdentity, 'displayName' | 'liveMatch'>> = {
    public:   { role: 'public',  isFounder: false, isMember: false, canBuildComp: false, isClanLeader: false, isTeamCaptain: false, canEnterComp: false, isOrgMember: false },
    // member persona is an ORG member — in the server-is-the-org model
    // (2026-07-19) entering via an org's Discord mints membership, so a
    // plain "member" preview must show the root "Apply to create a clan"
    // pill, not the legacy org-less view.
    member:   { role: 'member',  isFounder: false, isMember: true,  canBuildComp: false, isClanLeader: false, isTeamCaptain: false, canEnterComp: false, isOrgMember: true  },
    leader:   { role: 'leader',  isFounder: false, isMember: true,  canBuildComp: false, isClanLeader: true,  isTeamCaptain: false, canEnterComp: true,  isOrgMember: false },
    // The captain persona (F1, 2026-07-22) — the only way to preview the
    // captain door without minting a real grant. Deliberately role 'member'
    // with isClanLeader false: that IS a captain's shape, and previewing it
    // any other way would hide the very bug this persona exists to prove.
    captain:  { role: 'member',  isFounder: false, isMember: true,  canBuildComp: false, isClanLeader: false, isTeamCaptain: true,  canEnterComp: true,  isOrgMember: true  },
    orgadmin: { role: 'member',  isFounder: false, isMember: true,  canBuildComp: true,  isClanLeader: false, isTeamCaptain: false, canEnterComp: false, isOrgMember: true  },
    founder:  { role: 'founder', isFounder: true,  isMember: true,  canBuildComp: true,  isClanLeader: false, isTeamCaptain: false, canEnterComp: true,  isOrgMember: false },
  };
  return { displayName: `${persona} (preview)`, liveMatch: null, ...map[persona] };
}
